import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { selectActivityContentLocale, selectActivityFromDetailState, selectReviewSortAndFilterMatrix } from 'component/page/detail/store/detail.selectors';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { getReviewInfo } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.utils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesSeeReviewTextlinkClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesSeeReviewTextlinkClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { mapTitleFromContent } from 'gql/service/graph/activityService';
import { setIsScrolling } from 'component/page/detail/store/details';
import { Badges } from 'gql/autogen/Badges';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

interface DetailCardStateProps {
    title: string;
    contentLocale: string;
    cms: CmsValue;
    badges: Badges | undefined;
    activityId: number;
    isBadgeV2Enabled: boolean;
    reviewTotalReview: number;
    reviewAverageScore: string;
}

interface DetailCardDispatchProps {
    onSeeReviewClick: (seeReviewHref: string) => void;
}

interface OwnProps {
    seeReviewHref: string;
}

export type DetailsCardProps = OwnProps & DetailCardStateProps & DetailCardDispatchProps;

export const mapStateToProps = createSelector(
    selectActivityFromDetailState,
    selectActivityContentLocale,
    selectCmsFromBootstrap,
    selectReviewSortAndFilterMatrix,
    (activity, contentLocale, cms, reviewSortAndFilterMatrix): DetailCardStateProps => {
        // Detail endpoint data
        const activityContent = activity.content || {};
        const title = mapTitleFromContent(activityContent);
        const badges = activityContent?.badges;
        const activityId = activity.masterActivityId || 0;
        const isBadgeV2Enabled = isOn(FeatureName.BadgesV2DetailsPage);
        // Review endpoint data
        const reviewTotalAndAverageScore = getReviewInfo(contentLocale, cms, reviewSortAndFilterMatrix);
        return {
            title,
            contentLocale,
            cms,
            badges,
            activityId,
            isBadgeV2Enabled,
            reviewTotalReview: reviewTotalAndAverageScore.totalReview,
            reviewAverageScore: reviewTotalAndAverageScore.averageScore,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DetailCardDispatchProps => ({
    onSeeReviewClick: (seeReviewHref: string) => {
        trackElementEvent<ActivitiesSeeReviewTextlinkClickEventActivitiesFieldsV1>(ActivitiesSeeReviewTextlinkClickEventActivitiesManifestV1.ids, {});
        navigateToHashWithTabBarOffset(dispatch, `#${seeReviewHref}`, setIsScrolling, true);
        setTimeout(() => {
            const nextFocusElementWithButton = document.querySelector(`#${seeReviewHref} button`) as HTMLElement;
            const nextFocusElementWithButtonOrHeading = nextFocusElementWithButton || (document.querySelector(`#${seeReviewHref}`) as HTMLElement);
            nextFocusElementWithButtonOrHeading?.focus();
        }, 1000);
    },
});
